<template>
  <div class="animated fadeIn">

    <!-- Main area -->
    <b-card>
      <h2>My Account</h2>
      <b-row>
        <b-col md="5">
          <b-card>

            <div class="text-muted text-uppercase text-left font-weight-bold font-s">
              Account Created
            </div>
            <p>{{ moment.unix(teamInfo.joined).format('MMMM Do YYYY') }}</p>
          
            <div class="text-muted text-uppercase text-left font-weight-bold font-s mt-4">
              ChatAcuity is tracking:
            </div>
            <b-list-group class="list-group-flush w-75">
              <b-list-group-item><span class="badge badge-primary badge-pill mr-2">{{ teamChannelsMonitored.length }}</span> Analyzed Channels</b-list-group-item>
              <b-list-group-item><span class="badge badge-primary badge-pill mr-2">{{ teamChannelsUnmonitored.length }}</span> Unanalyzed Channels</b-list-group-item>
              <b-list-group-item><span class="badge badge-primary badge-pill mr-2">{{ nonDeletedTeamMembers.length }}</span> Billable Users</b-list-group-item>
              <b-list-group-item><span class="badge badge-primary badge-pill mr-2">{{ teamMembers.length - nonDeletedTeamMembers.length }}</span> Deleted Users</b-list-group-item>
              <b-list-group-item><span class="badge badge-primary badge-pill mr-2">{{ teamGroups.length }}</span> Groups</b-list-group-item>
            </b-list-group>

            <div class="text-muted text-uppercase text-left font-weight-bold font-s mt-4">
              Automatically Join New Channels
            </div>

            <template v-if="teamInfo.auto_monitor_new_channels">
              <p>ChatAcuity is configured to automatically join new public channels as soon as they are created.  This is the recommended setting.</p>
            </template>
            <template v-else>
              <p>ChatAcuity is not currently configured to automatically join new public channels as they are created.  With this configuration, you must invite the app to any new channels you wish to monitor.  You can enable this feature with the toggle below.</p>
            </template>

            
            <label id="filterWakeupsSwitch" class="switch switch-label switch-success">
              <input 
                :disabled="autoJoinUpdateInProgress" 
                type="checkbox" 
                class="switch-input" 
                v-model="autoJoinEnabled"
                @change="autoJoinChecked">
              <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>

            </label>



            <div v-if="teamChannels.length > teamChannelsMonitored.length || submittedUnjoinedChannelsReturned === true" class="mt-4">
              <hr />

              <template v-if="submittedUnjoinedChannels === false && submittedUnjoinedChannelsReturned === false">
                <span class="bg-info text-white p-1 mt-4 mb-4 rounded">
                  &nbsp;&nbsp;<i class="far fa-lightbulb mr-1"></i>
                  Notice - ChatAcuity is not currently analyzing all workspace channels. &nbsp;
                </span>
                <p class="mt-2">Slack's API requires Chatacuity to join a channel before it can see the channel's messages. Select any channel below to allow Chatacuity to begin analyzing activity.</p>

                <b-row>
                  <b-col cols="8">
                    <v-select 
                    label="name" 
                    :options="teamChannelsUnmonitored"
                    :value="selectedUnmonitoredChannels"
                    :multiple="true"
                    :clearable="true"
                    :searchable="true"
                    :close-on-select="false"
                    @input="unmonitoredChannelsSelected">
                      <template slot="option" slot-scope="option">
                        #{{ option.name }}
                      </template>
                    </v-select>
                  </b-col>

                  <b-col>
                    <button 
                      type="button" 
                      class="btn btn-info p-2 text-white w-75 mb-2" 
                      @click="selectAllUnmonitoredChannelsClicked">Select All
                    </button>
                    <br />
                    <button 
                      type="button" 
                      class="btn btn-success p-2 w-75" 
                      @click="joinChannelsClicked" 
                      :disabled="selectedUnmonitoredChannels.length == 0">Join Channels
                    </button>
                  </b-col>
                </b-row>
              </template>
              <template v-else-if="submittedUnjoinedChannels === true && submittedUnjoinedChannelsReturned === false">
                <!-- Loading spinner while submitting -->
                <div class="sk-folding-cube align-middle">
                  <div class="sk-cube1 sk-cube"></div>
                  <div class="sk-cube2 sk-cube"></div>
                  <div class="sk-cube4 sk-cube"></div>
                  <div class="sk-cube3 sk-cube"></div>
                </div>
              </template>
              <template v-else>
                <span class="bg-success p-1 mt-4 mb-4 rounded">
                  Great!  ChatAcuity will begin joining those channels immediately.
                </span>
              </template>

              
          </div>

          </b-card>
        </b-col>

        <b-col md="7">
          <b-card>
            <div class="text-muted text-uppercase text-left font-weight-bold font-s mb-4">
              Billing
            </div>

            <template v-if="billingDataLoaded">
              <template v-if="billingData.subscriptions.length == 0">

                <h5>Free Trial Expires In</h5>
                <p :class="{'text-danger':(trialTimeRemaining <= trialRemainingWarningDays)}">{{ trialTimeRemaining }} days</p>

                <template v-if="trialTimeRemaining <= trialRemainingWarningDays">
                  <div class="alert alert-danger w-75" role="alert">
                    Your free trial is almost up!  Please consider subscribing to avoid any interruption to your service.
                  </div>

                </template>

                <button 
                  type="button" 
                  class="btn btn-primary btn-lg mt-2" 
                  @click="subscribeClicked" 
                  :disabled="!updateSubscriptionDataLoaded">Subscribe
                </button>
              </template>
              <template v-else>
                <template v-if="billingData.is_delinquent">
                  <div class="alert alert-danger w-75" role="alert">
                    Your account has a delinquent balance, please update your payment details immediately to avoid any interruption to your service.
                  </div>
                </template>
                <template v-else>
                  <div class="alert alert-success w-75" role="alert">
                    Your account is in good standing!
                  </div>
                </template>

                <h4>Subscription Plan Details</h4>
                <b-list-group class="list-group-flush w-75">
                  <b-list-group-item class="d-flex justify-content-between">
                    <strong>Pricing Plan</strong> {{ subscriptionDetails.plan.nickname }} 
                  </b-list-group-item>
                  <b-list-group-item class="d-flex justify-content-between">
                    <strong>Price Per Month</strong>  ${{ subscriptionDetails.plan.amount / 100 }}.00 
                  </b-list-group-item>
                  <b-list-group-item class="d-flex justify-content-between">
                    <strong>Current Billing Period</strong>  {{ moment.unix(subscriptionDetails.current_period_start).format('MMMM Do YYYY') }} - {{ moment.unix(subscriptionDetails.current_period_end).format('MMMM Do YYYY') }} 
                  </b-list-group-item>
                  <b-list-group-item class="d-flex justify-content-between">
                    <strong>Collection Method</strong> {{ subscriptionDetails.collection_method.replace('_', ' ') }} 
                  </b-list-group-item>
                </b-list-group>

                <div class="d-flex justify-content-start mt-4">
                  <button 
                    type="button" 
                    class="btn btn-info mt-4 mr-4" 
                    @click="updatePaymentClicked"><span class="text-white">Update Payment Method</span>
                  </button>

                  <button 
                    type="button" 
                    class="btn btn-danger mt-4" 
                    @click="cancelSubscriptionClicked">Cancel Subscription
                  </button>

                </div>
                <!-- Cancel Subscription Modal -->
                <b-modal title="Confirm Cancellation" class="modal-danger" v-model="cancelSubscriptionModal" @ok="cancelSubscriptionConfirmed" ok-variant="danger">
                  Are you sure you wish to cancel your subscription?  Cancellation will result in immediate termination of service and purge of all data associated with your account!
                </b-modal>

              </template>
            </template>
            <template v-else>
              <div class="sk-folding-cube align-middle">
                <div class="sk-cube1 sk-cube"></div>
                <div class="sk-cube2 sk-cube"></div>
                <div class="sk-cube4 sk-cube"></div>
                <div class="sk-cube3 sk-cube"></div>
              </div>
            </template>

          </b-card>
        </b-col>
      </b-row>
    </b-card>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import { getStyle } from '@coreui/coreui-pro/dist/js/coreui-utilities'
import axios from 'axios'
import Cookies from 'js-cookie'

export default {
  name: 'AccountDashboard',
  components: {
  },
  data: function () {
    return {
      pageName: 'account',

      successColor: getStyle('--success'),
      dangerColor: getStyle('--danger'),

      autoJoinEnabled: false,
      autoJoinUpdateInProgress: false,

      stripe: null,

      trialRemainingWarningDays: 3,
      cancelSubscriptionModal: false,

      selectedUnmonitoredChannels: [],
      submittedUnjoinedChannels: false,
      submittedUnjoinedChannelsReturned: false
    }
    
  },

  computed: {
    teamInfo() {
      return this.$store.getters.teamInfo
    },
    teamMembers() {
      return this.$store.getters.teamMembers
    },
    nonDeletedTeamMembers() {
      return this.$store.getters.nonDeletedTeamMembers
    },
    teamGroups() {
      return this.$store.getters.teamGroups
    },
    teamChannels() {
      return this.$store.getters.teamChannels
    },
    teamChannelsMonitored() {
      return this.$store.getters.teamChannels.filter(chan => chan.mon == true)
    },
    teamChannelsUnmonitored() {
      return this.$store.getters.teamChannels.filter(chan => chan.mon == false)
    },

    billingData() {
      return this.$store.getters.billingData
    },
    billingDataLoaded() {
      return this.$store.getters.billingDataLoaded
    },

    billingData() {
      return this.$store.getters.billingData
    },
    billingDataLoaded() {
      return this.$store.getters.billingDataLoaded
    },

    updateSubscriptionData() {
      return this.$store.getters.updateSubscriptionData
    },
    updateSubscriptionDataLoaded() {
      return this.$store.getters.updateSubscriptionDataLoaded
    },

    subscriptionDetails() {
      return this.billingData.subscriptions[0]
    },

    trialTimeRemaining() {
      let created = moment.unix(this.teamInfo.joined)
      let trialEnds = created.add(15, 'd')  // 7 immediately shows 6 days remaining in dash
      let now = moment()

      let diff = trialEnds.diff(now, 'days')
      diff = Math.max(diff, 0)

      return diff
    }

  },
  
  methods: {
    unmonitoredChannelsSelected(value) {
      this.selectedUnmonitoredChannels = value
      // console.log(this.filterTeammates)
    },

    selectAllUnmonitoredChannelsClicked() {
      this.selectedUnmonitoredChannels = this.teamChannelsUnmonitored
    },

    joinChannelsClicked() {
      let uri = '/teams/' + this.teamInfo.id.toString() + '/channels/join'
      this.submittedUnjoinedChannels = true
      axios.post(uri, {
        channels: this.selectedUnmonitoredChannels.map(c => c.slack_id)
      })
      .then(response => {
        // console.log(response.data)
        this.submittedUnjoinedChannelsReturned = true
        this.$store.commit('markChannelsAsMonitored', this.selectedUnmonitoredChannels.map(c => c.id))
      })
    },

    autoJoinChecked: function(state) {
      // console.log(this.autoJoinEnabled)
      let uri = '/teams/' + this.teamInfo.id.toString() + '/settings'
      this.autoJoinUpdateInProgress = true
      axios.put(uri, {
        autoJoinNewChannels: this.autoJoinEnabled
      })
      .then(response => {
        console.log(response.data)
        this.autoJoinUpdateInProgress = false
        this.$store.commit('updateAutoMonitorNewChannels', this.autoJoinEnabled)
      })
    },

    subscribeClicked() {
      let uri = '/teams/' + this.teamInfo.id.toString() + '/billing/subscribe'

      axios.post(uri)
      .then(response => {
        // context.commit('updateSubscriptionData', response.data.data)
        // context.commit('updateSubscriptionDataLoaded', true)
        // console.log(response.data)

        this.stripe.redirectToCheckout({
          sessionId: response.data.data.id
        }).then(function (result) {
          // If `redirectToCheckout` fails due to a browser or network
          // error, display the localized error message to your customer
          // using `result.error.message`.
          console.log('Error redirecting to checkout!')
          console.log(result.error)
        });
      })
    },

    updatePaymentClicked() {
      console.log('update payment clicked!')
      let uri = '/teams/' + this.teamInfo.id.toString() + '/billing/subscribe?maintenance'

      axios.post(uri)
      .then(response => {
        // console.log(response.data)

        this.stripe.redirectToCheckout({
          sessionId: response.data.data.id
        }).then(function (result) {
          // If `redirectToCheckout` fails due to a browser or network
          // error, display the localized error message to your customer
          // using `result.error.message`.
          console.log('Error redirecting to checkout!')
          console.log(result.error)
        });
      })
    },

    cancelSubscriptionClicked() {
      this.cancelSubscriptionModal = true
    }, 

    cancelSubscriptionConfirmed() {
      console.log('cancel clicked!')
      let uri = '/teams/' + this.teamInfo.id.toString() + '/billing/subscribe?cancel'

      axios.post(uri)
      .then(response => {
        console.log(response.data)

        if(Number.isInteger(response.data.data.canceled_at)) {
          // was successfully cancelled.  log them out!  other stuff happens on backend
          Cookies.remove(this.$store.getters.authTokenCookieName)
          window.location = this.$store.getters.loginUrl
        }
      
      })
    }
  },

  mounted() {
    this.$store.commit('currentPage', this.pageName)

    // https://stackoverflow.com/questions/45047126/how-to-add-external-js-scripts-to-vuejs-components
    if(this.stripe === null) {
      this.stripe = Stripe('pk_live_q9MvIYhvoUroglw6SvC6evXR00ZykpytHf')
      // this.stripe = Stripe('pk_test_9cNmnWpLM9ZjeleodosaX1iG00s1kHjTd6')
    }

    this.$store.dispatch('updateBillingData', [])

    // set the auto join toggle
    this.autoJoinEnabled = this.teamInfo.auto_monitor_new_channels
    
  }
}
</script>
